import React, { useState, useEffect } from "react";
import { X, ArrowRightLeft, RefreshCw } from "lucide-react";
import api from "../utils/api";
import { toast } from "react-toastify";

export default function TableTransferModal({ orderId, currentTable, onClose, onTransferred }) {
  const [layout, setLayout] = useState(null);
  const [activeOrders, setActiveOrders] = useState({});
  const [loading, setLoading] = useState(true);
  const [transferring, setTransferring] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/layout"), api.get("/orders/active")])
      .then(([layoutRes, ordersRes]) => {
        setLayout(layoutRes.data);
        setActiveOrders(ordersRes.data.tables || {});
      })
      .catch(() => toast.error("Failed to load layout"))
      .finally(() => setLoading(false));
  }, []);

  const isFree = (floorName, sectionName, unitNumber, roomType) => {
    const key = `${floorName}-${sectionName}-${roomType || "NA"}-${unitNumber}`;
    return !activeOrders[key];
  };

  const isCurrent = (floorName, sectionName, unitNumber, roomType) => {
    if (!currentTable) return false;
    return (
      currentTable.floorName === floorName &&
      currentTable.sectionName === sectionName &&
      String(currentTable.unitNumber) === String(unitNumber) &&
      (currentTable.roomType || "NA") === (roomType || "NA")
    );
  };

  const handleTransfer = async () => {
    if (!selected) return;
    setTransferring(true);
    try {
      await api.patch(`/orders/${orderId}/transfer-table`, {
        floorName: selected.floorName,
        sectionName: selected.sectionName,
        sectionType: selected.sectionType,
        unitNumber: selected.unitNumber,
        roomType: selected.roomType || null,
      });
      toast.success(`Order moved to ${selected.sectionName} · ${selected.label}`);
      onTransferred(selected);
    } catch (err) {
      toast.error(err.response?.data?.message || "Transfer failed");
    } finally {
      setTransferring(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-indigo-500/15 border border-indigo-500/25 flex items-center justify-center">
              <ArrowRightLeft size={15} className="text-indigo-500" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-800">Transfer Table</h2>
              <p className="text-[10px] text-slate-400">Select a free table to move this order to</p>
            </div>
          </div>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400">
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <RefreshCw size={18} className="animate-spin text-slate-400" />
            </div>
          ) : !layout ? (
            <p className="text-xs text-slate-400 text-center py-8">No layout found</p>
          ) : (
            layout.floors?.map((floor) =>
              floor.sections?.map((section) => {
                const tables = Array.from({ length: section.totalUnits || 0 }, (_, i) => i + 1);
                if (!tables.length) return null;
                return (
                  <div key={`${floor.floorName}-${section.name}`}>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
                      {floor.floorName} · {section.name}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {tables.map((num) => {
                        const free = isFree(floor.floorName, section.name, num, section.roomType);
                        const current = isCurrent(floor.floorName, section.name, num, section.roomType);
                        const sel = selected &&
                          selected.floorName === floor.floorName &&
                          selected.sectionName === section.name &&
                          selected.unitNumber === num;
                        return (
                          <button
                            key={num}
                            disabled={!free || current}
                            onClick={() => setSelected({
                              floorName: floor.floorName,
                              sectionName: section.name,
                              sectionType: section.type,
                              unitNumber: num,
                              roomType: section.roomType || null,
                              label: `${section.roomType ? section.roomType + " " : ""}${num}`,
                            })}
                            className={`w-14 h-14 rounded-xl text-xs font-bold border transition-all flex flex-col items-center justify-center gap-0.5 ${
                              current
                                ? "bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed"
                                : !free
                                ? "bg-red-50 border-red-200 text-red-400 cursor-not-allowed"
                                : sel
                                ? "bg-indigo-500 border-indigo-500 text-white shadow-lg shadow-indigo-200"
                                : "bg-white border-slate-200 text-slate-700 hover:border-indigo-400 hover:bg-indigo-50"
                            }`}
                          >
                            <span className="text-sm font-black">{num}</span>
                            <span className="text-[8px] leading-none font-normal opacity-70">
                              {current ? "HERE" : !free ? "BUSY" : "FREE"}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between gap-3">
          <div className="text-xs text-slate-500">
            {selected
              ? <span>Moving to <strong className="text-slate-700">{selected.sectionName} · {selected.label}</strong></span>
              : <span className="text-slate-400">No table selected</span>
            }
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-xs font-semibold bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200">
              Cancel
            </button>
            <button
              onClick={handleTransfer}
              disabled={!selected || transferring}
              className="px-5 py-2 text-xs font-bold bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl disabled:opacity-40 flex items-center gap-1.5 transition-colors"
            >
              <ArrowRightLeft size={12} />
              {transferring ? "Moving..." : "Transfer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
